/**
 * Time Entry Analytics Service
 * Provides reusable time entry aggregation and analysis for allowance calculations
 * 
 * This service borrows patterns from accrualCalculationService.calculateWorkedHours
 * while extending functionality for allowance-specific needs including:
 * - Daily hour totals and worked-day eligibility
 * - Weekend/holiday flagging
 * - Split-shift detection
 */

import { supabase } from '@/integrations/supabase/client';
import { 
  format, 
  startOfDay, 
  endOfDay, 
  eachDayOfInterval, 
  parseISO, 
  differenceInMinutes 
} from 'date-fns';
import { devLog } from '@/utils/logger';
import type { 
  TimeEntryAnalyticsInput,
  TimeEntryAnalyticsResult,
  DayAnalytics,
  ShiftDetail,
  SplitShiftConfig 
} from './types';

interface BusinessHours {
  [key: string]: {
    enabled: boolean;
    start: string;
    end: string;
  };
}

export const timeEntryAnalytics = {
  /**
   * Fetch approved time entries for analysis
   * Reuses pattern from accrualCalculationService.getApprovedTimeEntries
   */
  async getApprovedTimeEntries(profileId: string, startDate: Date, endDate: Date) {
    const { data: timeEntries, error } = await supabase
      .from('time_entries')
      .select('id, clock_in, clock_out, profile_id')
      .eq('profile_id', profileId)
      .eq('status', 'approved')
      .gte('clock_in', startDate.toISOString())
      .lte('clock_out', endDate.toISOString())
      .not('clock_out', 'is', null)
      .order('clock_in', { ascending: true });

    if (error) throw error;
    return timeEntries || [];
  },

  /**
   * Get company business hours configuration
   * Reuses pattern from accrualCalculationService.getCompanyBusinessHours
   */
  async getCompanyBusinessHours(profileId: string): Promise<BusinessHours> {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', profileId)
      .single();

    if (error) throw error;

    const { data: companySettings, error: settingsError } = await supabase
      .from('company_settings')
      .select('business_hours')
      .eq('company_id', profile.company_id)
      .single();

    if (settingsError) throw settingsError;

    return companySettings?.business_hours as BusinessHours || {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    };
  },

  /**
   * Calculate standard daily hours from business hours config
   * Reuses utility from accrualCalculationService
   */
  calculateStandardDailyHours(businessDay: any): number {
    if (!businessDay?.enabled) return 0;
    
    const start = new Date(`2000-01-01T${businessDay.start}`);
    const end = new Date(`2000-01-01T${businessDay.end}`);
    
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  },

  /**
   * Detect split shifts for a given day
   * A split shift occurs when there are multiple shifts in one day separated by a gap
   */
  detectSplitShifts(shifts: ShiftDetail[], config?: SplitShiftConfig): number {
    if (!config?.enabled || shifts.length < 2) return 0;

    let splitCount = 0;
    const sortedShifts = [...shifts].sort((a, b) => a.clockIn.getTime() - b.clockIn.getTime());

    for (let i = 1; i < sortedShifts.length; i++) {
      const prevShift = sortedShifts[i - 1];
      const currentShift = sortedShifts[i];

      // Check minimum shift length
      const prevShiftMinutes = differenceInMinutes(prevShift.clockOut, prevShift.clockIn);
      const currentShiftMinutes = differenceInMinutes(currentShift.clockOut, currentShift.clockIn);

      if (prevShiftMinutes < config.minimumShiftLengthMinutes || 
          currentShiftMinutes < config.minimumShiftLengthMinutes) {
        continue;
      }

      // Check gap between shifts
      const gapMinutes = differenceInMinutes(currentShift.clockIn, prevShift.clockOut);
      
      if (gapMinutes >= config.minimumGapMinutes) {
        splitCount++;
        
        // If not counting multiple splits per day, return after first detection
        if (!config.countMultiplePerDay) {
          return 1;
        }
      }
    }

    return splitCount;
  },

  /**
   * Check if a date is a weekend
   */
  isWeekendDay(date: Date): boolean {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
  },

  /**
   * Main analytics function - aggregates time entry data with eligibility analysis
   * Borrows day-iteration pattern from accrualCalculationService.calculateWorkedHours
   * and extends it for allowance-specific needs
   */
  async analyzeTimeEntries(input: TimeEntryAnalyticsInput): Promise<TimeEntryAnalyticsResult> {
    const {
      profileId,
      startDate,
      endDate,
      excludeWeekends = false,
      excludeHolidays = false,
      minimumHoursPerDay = 0,
      splitShiftConfig
    } = input;

    // Fetch data
    const timeEntries = await this.getApprovedTimeEntries(profileId, startDate, endDate);
    const businessHours = await this.getCompanyBusinessHours(profileId);

    // Fetch company holidays from settings
    const { data: profileData } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('id', profileId)
      .single();

    const { data: settingsData } = await supabase
      .from('company_settings')
      .select('holidays, timezone')
      .eq('company_id', profileData?.company_id)
      .maybeSingle();

    const companyTimezone = (settingsData as any)?.timezone || 'UTC';

    // Parse holidays into Set of ISO date strings (YYYY-MM-DD format)
    const companyHolidays = new Set<string>(
      ((settingsData as any)?.holidays as string[]) || []
    );

    // Helper: get YYYY-MM-DD in company timezone (matches server getDateInTimezone)
    const getDateInTz = (isoStr: string): string => {
      return new Date(isoStr).toLocaleDateString('en-CA', { timeZone: companyTimezone });
    };

    devLog(`[Analytics] Loaded ${companyHolidays.size} company holidays for period`, {
      profileId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      companyTimezone,
      holidays: Array.from(companyHolidays)
    });

    // Initialize result
    const result: TimeEntryAnalyticsResult = {
      profileId,
      startDate,
      endDate,
      totalHours: 0,
      regularHours: 0,
      overtimeHours: 0,
      holidayHours: 0,
      nonScheduledHours: 0, // NEW: Track non-scheduled hours separately
      totalWorkedDays: 0,
      eligibleDaysCount: 0,
      splitShiftTotalCount: 0,
      weekendDaysWorked: 0,
      holidayDaysWorked: 0,
      dayAnalytics: [],
      calculationSummary: {
        daysInPeriod: 0,
        businessDaysInPeriod: 0,
        excludedDays: {
          weekends: 0,
          holidays: 0,
          belowMinimumHours: 0
        }
      }
    };

    // Pre-group entries by company-timezone date (matches server logic)
    const entryByDay = new Map<string, typeof timeEntries>();
    for (const entry of timeEntries) {
      const key = getDateInTz(entry.clock_in);
      if (!entryByDay.has(key)) entryByDay.set(key, []);
      entryByDay.get(key)!.push(entry);
    }

    // Generate timezone-aware date strings for iteration (immune to DST/browser-TZ issues)
    const startStr = startDate.toLocaleDateString('en-CA', { timeZone: companyTimezone });
    const endStr = endDate.toLocaleDateString('en-CA', { timeZone: companyTimezone });
    const periodDates: string[] = [];
    {
      const [sy, sm, sd] = startStr.split('-').map(Number);
      const [ey, em, ed] = endStr.split('-').map(Number);
      const cursor = new Date(Date.UTC(sy, sm - 1, sd));
      const endRef = new Date(Date.UTC(ey, em - 1, ed));
      while (cursor <= endRef) {
        periodDates.push(cursor.toISOString().split('T')[0]);
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
    }

    result.calculationSummary.daysInPeriod = periodDates.length;

    const dayNamesLookup = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    for (const dateKey of periodDates) {
      // Derive day-of-week from date string (timezone-safe)
      const [yr, mo, dy] = dateKey.split('-').map(Number);
      const dateRef = new Date(Date.UTC(yr, mo - 1, dy));
      const dayOfWeekIndex = dateRef.getUTCDay();
      const dayName = dayNamesLookup[dayOfWeekIndex];

      // Get entries for this day from pre-grouped map
      const dayEntries = entryByDay.get(dateKey) || [];

      // Build shift details
      const shifts: ShiftDetail[] = dayEntries.map(entry => ({
        clockIn: parseISO(entry.clock_in),
        clockOut: parseISO(entry.clock_out),
        hours: (parseISO(entry.clock_out).getTime() - parseISO(entry.clock_in).getTime()) / (1000 * 60 * 60),
        entryId: entry.id
      }));

      // Calculate hours for the day
      let dayRegularHours = 0;
      let dayOvertimeHours = 0;

      for (const shift of shifts) {
        dayRegularHours += shift.hours;
      }

      // Determine overtime based on business hours (pattern from accrualCalculationService)
      const businessDay = businessHours[dayName];
      const standardDailyHours = this.calculateStandardDailyHours(businessDay);
      
      // Flag analysis (moved up to use in isBusinessDay calculation)
      const isWeekend = dayOfWeekIndex === 0 || dayOfWeekIndex === 6;
      const isHoliday = companyHolidays.has(dateKey);
      
      // CRITICAL: Holidays are NOT business days (aligned with server logic)
      // This ensures per-worked-day allowances calculate identically in preview and payroll
      const isBusinessDay = (businessDay?.enabled || false) && !isHoliday;

      if (dayRegularHours > standardDailyHours && isBusinessDay) {
        dayOvertimeHours = dayRegularHours - standardDailyHours;
        dayRegularHours = standardDailyHours;
      }

      const dayTotalHours = dayRegularHours + dayOvertimeHours;
      const hasSufficientHours = dayTotalHours >= minimumHoursPerDay;
      
      // Detect split shifts
      const splitShiftCount = splitShiftConfig 
        ? this.detectSplitShifts(shifts, splitShiftConfig)
        : 0;

      // Determine eligibility for allowance
      const isEligible = 
        shifts.length > 0 && 
        hasSufficientHours &&
        (!excludeWeekends || !isWeekend) &&
        (!excludeHolidays || !isHoliday);

      // Build day analytics
      const dayAnalytic: DayAnalytics = {
        date: dateKey,
        dayOfWeek: dayName,
        totalHours: dayTotalHours,
        regularHours: dayRegularHours,
        overtimeHours: dayOvertimeHours,
        shiftCount: shifts.length,
        splitShiftCount,
        isWeekend,
        isHoliday,
        isBusinessDay,
        isEligibleForAllowance: isEligible,
        shifts
      };

      result.dayAnalytics.push(dayAnalytic);

      // Update aggregate counters
      if (shifts.length > 0) {
        result.totalWorkedDays++;
        result.totalHours += dayTotalHours;
        
        // Only add to regularHours/overtimeHours if NOT a holiday
        // Holiday hours are tracked separately to enable proper compensation rules
        if (!isHoliday) {
          result.regularHours += dayRegularHours;
          result.overtimeHours += dayOvertimeHours;
        }
        
        result.splitShiftTotalCount += splitShiftCount;

        // Track non-scheduled hours separately (weekend + holiday work on non-business days)
        if (!isBusinessDay && shifts.length > 0) {
          result.nonScheduledHours += dayTotalHours;
        }

        if (isWeekend) result.weekendDaysWorked++;
        if (isHoliday) {
          result.holidayDaysWorked++;
          result.holidayHours += dayTotalHours; // Aggregate holiday hours
        }
      }

      if (isEligible) {
        result.eligibleDaysCount++;
      } else if (shifts.length > 0) {
        // Track why day was excluded
        if (excludeWeekends && isWeekend) {
          result.calculationSummary.excludedDays.weekends++;
        } else if (excludeHolidays && isHoliday) {
          result.calculationSummary.excludedDays.holidays++;
        } else if (!hasSufficientHours) {
          result.calculationSummary.excludedDays.belowMinimumHours++;
        }
      }

      if (isBusinessDay) {
        result.calculationSummary.businessDaysInPeriod++;
      }
    }

    return result;
  },

  /**
   * Batch processing for multiple employees
   * Useful for payroll generation across the company
   */
  async analyzeMultipleEmployees(
    profileIds: string[],
    startDate: Date,
    endDate: Date,
    commonConfig?: Omit<TimeEntryAnalyticsInput, 'profileId' | 'startDate' | 'endDate'>
  ): Promise<Map<string, TimeEntryAnalyticsResult>> {
    const results = new Map<string, TimeEntryAnalyticsResult>();
    
    // Process in batches to avoid overwhelming the database
    const batchSize = 10;
    for (let i = 0; i < profileIds.length; i += batchSize) {
      const batch = profileIds.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (profileId) => {
        try {
          const analytics = await this.analyzeTimeEntries({
            profileId,
            startDate,
            endDate,
            ...commonConfig
          });
          return { profileId, analytics };
        } catch (error) {
          console.error(`Error analyzing time entries for profile ${profileId}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      
      for (const result of batchResults) {
        if (result) {
          results.set(result.profileId, result.analytics);
        }
      }
    }
    
    return results;
  }
};
